import { createSlice } from '@reduxjs/toolkit';
import {
  getApprovedRefinancesThunk,
  getApprovedReviewRefinancesThunk,
  getCancelledRefinancesThunk,
  getPendingRefinancesThunk,
  getReferbackRefinancesThunk,
  getRejectedRefinancesThunk,
  getOpenRefinancesThunk,
  getSubmitForApprovalRefinancesThunk,
  getRefinanceFinanceListingThunk,
  getAllRefinancesThunk,
} from '../../services/refinanceService/refinanceThunk';
import { Application } from '../application/applicationSlice';
import { Agreement } from '../agreement/agreementSlice';
import { AccountDetails } from '../collection/collectionSlice';

export type Refinance = {
  id: string;
  applicationId: string;
  application: Application;
  oldAgreementId?: string;
  oldAgreement?: Agreement;
  oldAgreementNo?: string;
  accountDetailsId: string;
  effectiveDate: string;
  totalLoanOutstanding: number;
  refinanceFee: number;
  preExecutionPayment: number;
  oldAccountMia?: number;
  oldAccountTotalDueInstalment?: number;
  oldAccountPaidInstalmentMonth?: number;
  oldAccountTotalPaidInstalment?: number;
  oldAccountLastPaymentDate?: string;
  oldAccountLastPaymentAmount?: number;
  oldAccountTotalInstalmentMonth?: number;
  oldAccountCurrentTenureNo?: number;
  oldAccountInstalmentBalance?: number;
  oldAccountTotalCharges?: number;
  createdAt: string;
  updatedAt: string;
};

export type RefinanceWithAccountDetails = Refinance & {
  accountDetails: AccountDetails;
};

export type RefinanceListing = {
  searchTotal: number;
  total: number;
  currentPage: number;
  pageSize: number;
  data: Refinance[];
};

export type RefinanceState = {
  refinanceList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: RefinanceListing;
  };
  openRefinanceList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: RefinanceListing;
  };
  pendingRefinanceList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: RefinanceListing;
  };
  approvedRefinanceList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: RefinanceListing;
  };
  rejectedRefinanceList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: RefinanceListing;
  };
  cancelledRefinanceList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: RefinanceListing;
  };
  referBackRefinanceList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: RefinanceListing;
  };
  submitForApprovalRefinanceList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data: RefinanceListing;
  };
  approvedReviewRefinanceList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data?: RefinanceListing;
  };
  refinanceFinanceList: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: any;
    data?: RefinanceListing;
  };
};

const initialState: RefinanceState = {
  refinanceList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  openRefinanceList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  pendingRefinanceList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  approvedRefinanceList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  rejectedRefinanceList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  cancelledRefinanceList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  referBackRefinanceList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  submitForApprovalRefinanceList: {
    isLoading: false,
    isError: false,
    errorMessage: undefined,
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  approvedReviewRefinanceList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
  refinanceFinanceList: {
    isLoading: false,
    isError: false,
    errorMessage: '',
    data: {
      searchTotal: 0,
      total: 0,
      currentPage: 0,
      pageSize: 0,
      data: [],
    },
  },
};

export const refinanceSlice = createSlice({
  name: 'refinance',
  initialState,
  reducers: {
    refinanceStateReset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllRefinancesThunk.pending, (state) => {
        state.refinanceList.isLoading = true;
      })
      .addCase(getAllRefinancesThunk.fulfilled, (state, action) => {
        state.refinanceList.isLoading = false;
        state.refinanceList.data = action.payload;
      })
      .addCase(getAllRefinancesThunk.rejected, (state, action) => {
        state.refinanceList.isLoading = false;
        state.refinanceList.isError = true;
        state.refinanceList.errorMessage = action.payload;
        state.refinanceList.data.data = [];
      });
      
    builder
      .addCase(getOpenRefinancesThunk.pending, (state) => {
        state.openRefinanceList.isLoading = true;
      })
      .addCase(getOpenRefinancesThunk.fulfilled, (state, action) => {
        state.openRefinanceList.isLoading = false;
        state.openRefinanceList.data = action.payload;
      })
      .addCase(getOpenRefinancesThunk.rejected, (state, action) => {
        state.openRefinanceList.isLoading = false;
        state.openRefinanceList.isError = true;
        state.openRefinanceList.errorMessage = action.payload;
        state.openRefinanceList.data.data = [];
      });

    builder
      .addCase(getPendingRefinancesThunk.pending, (state) => {
        state.pendingRefinanceList.isLoading = true;
      })
      .addCase(getPendingRefinancesThunk.fulfilled, (state, action) => {
        state.pendingRefinanceList.isLoading = false;
        state.pendingRefinanceList.data = action.payload;
      })
      .addCase(getPendingRefinancesThunk.rejected, (state, action) => {
        state.pendingRefinanceList.isLoading = false;
        state.pendingRefinanceList.isError = true;
        state.pendingRefinanceList.errorMessage = action.payload;
        state.pendingRefinanceList.data.data = [];
      });

    builder
      .addCase(getApprovedRefinancesThunk.pending, (state) => {
        state.approvedRefinanceList.isLoading = true;
      })
      .addCase(getApprovedRefinancesThunk.fulfilled, (state, action) => {
        state.approvedRefinanceList.isLoading = false;
        state.approvedRefinanceList.data = action.payload;
      })
      .addCase(getApprovedRefinancesThunk.rejected, (state, action) => {
        state.approvedRefinanceList.isLoading = false;
        state.approvedRefinanceList.isError = true;
        state.approvedRefinanceList.errorMessage = action.payload;
        state.approvedRefinanceList.data.data = [];
      });

    builder
      .addCase(getReferbackRefinancesThunk.pending, (state) => {
        state.referBackRefinanceList.isLoading = true;
      })
      .addCase(getReferbackRefinancesThunk.fulfilled, (state, action) => {
        state.referBackRefinanceList.isLoading = false;
        state.referBackRefinanceList.data = action.payload;
      })
      .addCase(getReferbackRefinancesThunk.rejected, (state, action) => {
        state.referBackRefinanceList.isLoading = false;
        state.referBackRefinanceList.isError = true;
        state.referBackRefinanceList.errorMessage = action.payload;
        state.referBackRefinanceList.data.data = [];
      });

    builder
      .addCase(getRejectedRefinancesThunk.pending, (state) => {
        state.rejectedRefinanceList.isLoading = true;
      })
      .addCase(getRejectedRefinancesThunk.fulfilled, (state, action) => {
        state.rejectedRefinanceList.isLoading = false;
        state.rejectedRefinanceList.data = action.payload;
      })
      .addCase(getRejectedRefinancesThunk.rejected, (state, action) => {
        state.rejectedRefinanceList.isLoading = false;
        state.rejectedRefinanceList.isError = true;
        state.rejectedRefinanceList.errorMessage = action.payload;
        state.rejectedRefinanceList.data.data = [];
      });

    builder
      .addCase(getCancelledRefinancesThunk.pending, (state) => {
        state.cancelledRefinanceList.isLoading = true;
      })
      .addCase(getCancelledRefinancesThunk.fulfilled, (state, action) => {
        state.cancelledRefinanceList.isLoading = false;
        state.cancelledRefinanceList.data = action.payload;
      })
      .addCase(getCancelledRefinancesThunk.rejected, (state, action) => {
        state.cancelledRefinanceList.isLoading = false;
        state.cancelledRefinanceList.isError = true;
        state.cancelledRefinanceList.errorMessage = action.payload;
        state.cancelledRefinanceList.data.data = [];
      });

    builder
      .addCase(getSubmitForApprovalRefinancesThunk.pending, (state) => {
        state.submitForApprovalRefinanceList.isLoading = true;
      })
      .addCase(getSubmitForApprovalRefinancesThunk.fulfilled, (state, action) => {
        state.submitForApprovalRefinanceList.isLoading = false;
        state.submitForApprovalRefinanceList.data = action.payload;
      })
      .addCase(getSubmitForApprovalRefinancesThunk.rejected, (state, action) => {
        state.submitForApprovalRefinanceList.isLoading = false;
        state.submitForApprovalRefinanceList.isError = true;
        state.submitForApprovalRefinanceList.errorMessage = action.payload;
        state.submitForApprovalRefinanceList.data.data = [];
      });

    builder
      .addCase(getApprovedReviewRefinancesThunk.pending, (state) => {
        state.approvedReviewRefinanceList.isLoading = true;
      })
      .addCase(getApprovedReviewRefinancesThunk.fulfilled, (state, action) => {
        state.approvedReviewRefinanceList.isLoading = false;
        state.approvedReviewRefinanceList.data = action.payload;
      })
      .addCase(getApprovedReviewRefinancesThunk.rejected, (state, action) => {
        state.approvedReviewRefinanceList.isLoading = false;
        state.approvedReviewRefinanceList.isError = true;
        state.approvedReviewRefinanceList.errorMessage = action.payload;
      });

    builder
      .addCase(getRefinanceFinanceListingThunk.pending, (state) => {
        state.refinanceFinanceList.isLoading = true;
      })
      .addCase(getRefinanceFinanceListingThunk.fulfilled, (state, action) => {
        state.refinanceFinanceList.isLoading = false;
        state.refinanceFinanceList.data = action.payload;
      })
      .addCase(getRefinanceFinanceListingThunk.rejected, (state, action) => {
        state.refinanceFinanceList.isLoading = false;
        state.refinanceFinanceList.isError = true;
        state.refinanceFinanceList.errorMessage = action.payload;
      });
  },
});

export const { refinanceStateReset } = refinanceSlice.actions;

export default refinanceSlice.reducer;
